import React, {useEffect, useRef} from 'react';
import {useForm, Controller} from 'react-hook-form';
import {
  Box,
  Button,
  Chip,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';

export interface ISoilData {
  soilType?: string;
  pH?: number;
  P?: number;
  K?: number;
  Ca?: number;
  Mg?: number;
  Na?: number;
  cations?: number;
  exchangeableAcidity?: number;
  anions?: number;
  Zn?: number;
  Mn?: number;
  Cu?: number;
}

const SOIL_TYPES = ['Clay', 'Loamy', 'Sandy', 'Peat', 'Chalky'];

const SOIL_FIELD_KEYS: Array<keyof ISoilData> = [
  'soilType',
  'pH',
  'P',
  'K',
  'Ca',
  'Mg',
  'Na',
  'cations',
  'exchangeableAcidity',
  'anions',
  'Zn',
  'Mn',
  'Cu',
];

const EMPTY_SOIL_DATA: ISoilData = {
  soilType: '',
  pH: undefined,
  P: undefined,
  K: undefined,
  Ca: undefined,
  Mg: undefined,
  Na: undefined,
  cations: undefined,
  exchangeableAcidity: undefined,
  anions: undefined,
  Zn: undefined,
  Mn: undefined,
  Cu: undefined,
};

// Build a normalized object so react-hook-form has a value for every field
// (avoids uncontrolled -> controlled flips on the underlying inputs).
const normalizeSoilData = (data?: ISoilData): ISoilData => {
  const next: ISoilData = {...EMPTY_SOIL_DATA};
  if (!data) return next;
  for (const key of SOIL_FIELD_KEYS) {
    const value = (data as Record<string, unknown>)[key as string];
    if (value === undefined || value === null) continue;
    (next as Record<string, unknown>)[key as string] = value;
  }
  return next;
};

const soilDataEqual = (a?: ISoilData, b?: ISoilData): boolean => {
  const left = normalizeSoilData(a);
  const right = normalizeSoilData(b);
  for (const key of SOIL_FIELD_KEYS) {
    const lv = (left as Record<string, unknown>)[key as string];
    const rv = (right as Record<string, unknown>)[key as string];
    const lEmpty = lv === undefined || lv === null || lv === '';
    const rEmpty = rv === undefined || rv === null || rv === '';
    if (lEmpty && rEmpty) continue;
    if (String(lv) !== String(rv)) return false;
  }
  return true;
};

interface Props {
  initialSoilData?: ISoilData;
  onSubmit: (data: ISoilData) => void;
  onCancel?: () => void;
  onNext?: (data: ISoilData) => void;
  /** When true, used inside AddNewDataPointForm: no Next/Add button; values sync via onValuesChange */
  hidePrimaryAction?: boolean;
  onValuesChange?: (data: ISoilData) => void;
  /** Flatten card chrome when nested in parent modal */
  embedded?: boolean;
}

export const AddNewFieldSoilData = ({
  initialSoilData,
  onSubmit,
  onCancel,
  onNext,
  hidePrimaryAction,
  onValuesChange,
  embedded,
}: Props) => {
  const {control, handleSubmit, reset, watch} = useForm<ISoilData>({
    defaultValues: normalizeSoilData(initialSoilData),
  });

  // Keep latest values that we've already pushed to the parent so we can
  // tell apart "parent echoed our state back" from "parent loaded new data".
  const lastSyncedRef = useRef<ISoilData>(normalizeSoilData(initialSoilData));

  useEffect(() => {
    if (!initialSoilData) return;
    if (soilDataEqual(initialSoilData, lastSyncedRef.current)) return;
    const normalized = normalizeSoilData(initialSoilData);
    lastSyncedRef.current = normalized;
    reset(normalized);
  }, [initialSoilData, reset]);

  useEffect(() => {
    if (!onValuesChange) return;
    const subscription = watch((value, {type}) => {
      // Only notify the parent when the user actually edits a field.
      // `reset` and programmatic updates fire with type === undefined.
      if (type !== 'change') return;
      const next = normalizeSoilData(value as ISoilData);
      lastSyncedRef.current = next;
      onValuesChange(next);
    });
    return () => subscription.unsubscribe();
  }, [watch, onValuesChange]);

  const handleClear = () => {
    const cleared = normalizeSoilData();
    lastSyncedRef.current = cleared;
    reset(cleared);
    if (onValuesChange) onValuesChange(cleared);
  };

  return (
    <Box
      p={embedded ? 0 : 3}
      borderRadius={embedded ? 0 : 12}
      boxShadow={embedded ? 0 : 1}
      bgcolor={embedded ? 'transparent' : '#FAFAFA'}
      maxWidth={embedded ? 'none' : 600}
      margin={embedded ? 0 : 'auto'}
    >
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6" style={{fontWeight: 600}}>
          Input soil data
        </Typography>
      </Box>

      <form
        onSubmit={handleSubmit((data) => {
          onSubmit(data);
        })}
      >
        {/* SOIL TYPE */}
        <Box mb={3}>
          <Typography style={{fontSize: 14, fontWeight: 500, marginBottom: 8}}>
            Soil Type
          </Typography>
          <Box display="flex" flexWrap="wrap">
            {SOIL_TYPES.map((type) => (
              <Controller
                key={type}
                name="soilType"
                control={control}
                render={({field: {onChange, value}}) => (
                  <Chip
                    label={type}
                    clickable
                    onClick={() => onChange(type)}
                    style={{
                      marginRight: 8,
                      marginBottom: 8,
                      backgroundColor: value === type ? '#2D9B73' : undefined,
                      color: value === type ? '#fff' : undefined,
                    }}
                  />
                )}
              />
            ))}
          </Box>
        </Box>

        {/* NUTRIENTS */}
        <Typography style={{fontSize: 14, fontWeight: 500, marginBottom: 8}}>
          Nutrients
        </Typography>
        <Grid container spacing={2}>
          {[
            {name: 'P', label: 'Phosphorus (P)'},
            {name: 'K', label: 'Potassium (K)'},
            {name: 'Ca', label: 'Calcium (Ca)'},
            {name: 'Mg', label: 'Magnesium (Mg)'},
            {name: 'Na', label: 'Sodium (Na)'},
            {name: 'cations', label: 'Cations'},
            {name: 'anions', label: 'Anions'},
            {name: 'Zn', label: 'Zinc'},
            {name: 'Mn', label: 'Manganese'},
            {name: 'Cu', label: 'Copper'},
            {name: 'exchangeableAcidity', label: 'Exchangeable Acidity'},
            {name: 'pH', label: 'pH'},
          ].map(({name, label}) => (
            <Grid item xs={12} sm={6} key={name}>
              <Controller
                name={name as keyof ISoilData}
                control={control}
                render={({field: {value, onChange, ...field}}) => (
                  <TextField
                    {...field}
                    value={value ?? ''}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') {
                        onChange(undefined);
                        return;
                      }
                      const num = Number(raw);
                      onChange(Number.isNaN(num) ? raw : num);
                    }}
                    type="number"
                    label={label}
                    fullWidth
                    variant="outlined"
                    placeholder="Enter value"
                    size="small"
                    InputProps={{
                      style: {backgroundColor: '#fff', borderRadius: 8},
                    }}
                  />
                )}
              />
            </Grid>
          ))}
        </Grid>

        {/* Action buttons */}
        <Box
          mt={4}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Button
            onClick={handleClear}
            style={{color: '#2D9B73', fontWeight: 600}}
          >
            Clear All
          </Button>
          <Box display="flex" gridColumnGap={8}>
            {onCancel && (
              <Button
                onClick={onCancel}
                variant="outlined"
                style={{
                  borderRadius: 24,
                  padding: '6px 18px',
                  borderColor: '#ccc',
                  backgroundColor: '#f9f9f9',
                }}
              >
                Back
              </Button>
            )}
            {!hidePrimaryAction && (
              <Button
                variant="contained"
                color="primary"
                type={onNext ? 'button' : 'submit'}
                style={{
                  backgroundColor: '#2D9B73',
                  borderRadius: 24,
                  padding: '6px 24px',
                }}
                onClick={
                  onNext
                    ? (e) => {
                        e.preventDefault();
                        handleSubmit((data) => onNext(data))(e);
                      }
                    : undefined
                }
              >
                {onNext ? 'Next' : 'Add'}
              </Button>
            )}
          </Box>
        </Box>
      </form>
    </Box>
  );
};
